import { useCallback, useEffect, useState } from 'react'
import Big from 'big.js'
import { amountFromWei } from '../../../utils/amount'
import { IPool } from '../../../types/reducerTypes'
import useWeb3 from '../../../hooks/useWeb3'
import { countProgress } from '../../../utils/progress'
import { USDB_DECIMALS } from '../../../config'
import { useGetContractByNameQuery } from '../../../modules/web3/api'
import { createContract } from '../../Dashboard/Launches'
import useNetworkId from '../../../hooks/useNetworkId'
import { ethers } from 'ethers'

const usePoolRaisePercentages = ({
  poolAddress,
  totalSupplyForSale,
  price,
  isEthRaise = false,
  isLabsPool = false,
}: {
  poolAddress: IPool['poolAddress']
  totalSupplyForSale: IPool['poolSettings']['totalSupplyForSale']
  price: IPool['privateSale']['price']
  isEthRaise?: boolean
  isLabsPool?: boolean
}) => {
  const { blastProvider } = useWeb3()
  const [tokensSold, setTokensSold] = useState(0)
  const isPoolValid = blastProvider && ethers.isAddress(poolAddress)

  const contractToFetch = isLabsPool
    ? 'TokenSale'
    : isEthRaise
    ? 'TokenSaleETH'
    : 'TokenSaleUSDB'

  const { networkId } = useNetworkId()
  const { data: tokenSaleUsdbContractData, error: tokenSaleUsdbContractError } =
    useGetContractByNameQuery({ name: contractToFetch, chainId: networkId })

  const tokenSaleAbi = tokenSaleUsdbContractData?.contract?.abi
  const { provider } = useWeb3()

  const fetchTokensSold = useCallback(async () => {
    const contract = createContract(tokenSaleAbi, poolAddress, provider)

    const methodName = isLabsPool ? 'getState' : 'state'

    const state = await contract?.[methodName]()
    const value = state[0]

    setTokensSold(value)
  }, [tokenSaleAbi, poolAddress, provider, isLabsPool])

  useEffect(() => {
    if (!isPoolValid || !tokenSaleAbi) return
    fetchTokensSold()
  }, [fetchTokensSold, isPoolValid, tokenSaleAbi])

  useEffect(() => {
    if (!tokenSaleUsdbContractError) return
  }, [tokenSaleUsdbContractError])

  const softCap = totalSupplyForSale ?? 0
  const hardCap = totalSupplyForSale ?? 0
  const { percentSoftCap, percentHardCap } = getPercentages({
    price,
    tokensSold,
    softCap,
    hardCap,
    isLabsPool,
  })
  const isDiluted = percentHardCap > 100

  if (!isPoolValid) {
    return {
      percentSoftCap: 0,
      percentHardCap: 0,
      isDiluted: false,
    }
  }

  return {
    percentSoftCap,
    percentHardCap,
    isDiluted,
  }
}

export default usePoolRaisePercentages

function getPercentages({ price, tokensSold, softCap, hardCap, isLabsPool }) {
  const soldTokens = new Big(
    isLabsPool
      ? tokensSold
      : amountFromWei(tokensSold, USDB_DECIMALS).toString(),
  )

  if (!price || price === 0 || price === '0' || soldTokens.toString() === '0') {
    return {
      sold: 0,
      percentSoftCap: 0,
      percentHardCap: 0,
    }
  }

  const sold = new Big(soldTokens).div(price)

  const percentSoftCap = countProgress(sold, new Big(softCap))
  const percentHardCap = countProgress(sold, new Big(hardCap))
  return {
    sold,
    percentSoftCap,
    percentHardCap,
  }
}
